import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import type { User } from '@/types';
import { post, get } from '@/lib/api-client';
import { websocketClient } from '@/services/websocket-client';

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  refreshAccessToken: () => Promise<boolean>;
  // Impersonation state
  isImpersonating: boolean;
  impersonatedUser: User | null;
  realUser: User | null;
  startImpersonation: (targetUser: User, expiresAt: string) => void;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Token storage keys
const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_DATA_KEY = 'user_data';
const TOKEN_EXPIRY_KEY = 'token_expiry';

// Impersonation storage keys
const IMPERSONATION_STATE_KEY = 'impersonation_state';

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedUser: User;
  realUser: User;
  expiresAt: string;
}

// Refresh token 5 minutes before expiry
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [realUser, setRealUser] = useState<User | null>(null);

  // Schedule automatic token refresh
  const scheduleTokenRefresh = useCallback((expiresIn: number) => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Calculate when to refresh (5 minutes before expiry)
    const refreshTime = Math.max(0, expiresIn * 1000 - REFRESH_BUFFER_MS);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        await refreshAccessToken();
      } catch (error) {
        console.error('Automatic token refresh failed:', error);
        // If refresh fails, logout user
        await logout();
      }
    }, refreshTime);
  }, []);

  // Refresh access token using refresh token
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      if (!refreshToken) {
        return false;
      }

      const response = await post<{ data: AuthTokens }>('/auth/refresh', { refreshToken });
      
      const { accessToken, refreshToken: newRefreshToken, expiresIn } = response.data;
      
      // Store new tokens
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
      
      // Update token state
      setToken(accessToken);
      
      const expiryTime = Date.now() + expiresIn * 1000;
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

      // Schedule next refresh
      scheduleTokenRefresh(expiresIn);

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, [scheduleTokenRefresh]);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      const userData = localStorage.getItem(USER_DATA_KEY);
      const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);

      if (token && userData && expiryTime) {
        try {
          const parsedUser = JSON.parse(userData);
          const expiry = parseInt(expiryTime, 10);
          const now = Date.now();

          // Check if token is expired or about to expire
          if (expiry - now < REFRESH_BUFFER_MS) {
            // Try to refresh token
            const refreshed = await refreshAccessToken();
            
            if (refreshed) {
              // Fetch fresh user data
              try {
                const response = await get<{ data: { user: User } }>('/auth/me');
                setUser(response.data.user);
                
                // Update token state with refreshed token
                const currentToken = localStorage.getItem(ACCESS_TOKEN_KEY);
                setToken(currentToken);
                
                // Connect WebSocket with current token
                if (currentToken) {
                  websocketClient.connect(currentToken);
                }
              } catch {
                // If fetching user fails, use cached data
                setUser(parsedUser);
                setToken(token);
                
                // Connect WebSocket with current token
                const currentToken = localStorage.getItem(ACCESS_TOKEN_KEY);
                if (currentToken) {
                  websocketClient.connect(currentToken);
                }
              }
            } else {
              // Refresh failed, clear auth state
              clearAuthData();
            }
          } else {
            // Token is still valid
            setUser(parsedUser);
            setToken(token);
            
            // Schedule refresh
            const expiresIn = Math.floor((expiry - now) / 1000);
            scheduleTokenRefresh(expiresIn);

            // Connect WebSocket
            websocketClient.connect(token);
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          clearAuthData();
        }
      }

      setIsLoading(false);
    };

    initializeAuth();

    // Restore impersonation state from localStorage and verify with backend
    const restoreImpersonation = async () => {
      const stored = localStorage.getItem(IMPERSONATION_STATE_KEY);
      if (!stored) return;

      try {
        const state: ImpersonationState = JSON.parse(stored);
        // Verify session is still active on the backend
        const response = await get<{ success: boolean; data: { isActive: boolean } }>('/admin/impersonate/status');
        if (response.data.isActive) {
          setIsImpersonating(true);
          setImpersonatedUser(state.impersonatedUser);
          setRealUser(state.realUser);
          setUser(state.impersonatedUser);
        } else {
          // Backend session ended (expired or stopped elsewhere), clean up local state
          localStorage.removeItem(IMPERSONATION_STATE_KEY);
        }
      } catch {
        // If verification fails, clear stale impersonation state
        localStorage.removeItem(IMPERSONATION_STATE_KEY);
      }
    };

    restoreImpersonation();

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [refreshAccessToken, scheduleTokenRefresh]);

  const clearAuthData = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(IMPERSONATION_STATE_KEY);
    setUser(null);
    setToken(null);
    setIsImpersonating(false);
    setImpersonatedUser(null);
    setRealUser(null);
    
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Disconnect WebSocket
    websocketClient.disconnect();
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await post<{ 
        success: boolean;
        data: {
          tokens: AuthTokens;
          user: User;
        };
      }>('/auth/login', { email, password });

      if (!response.success) {
        throw new Error('Login failed');
      }

      const { tokens, user: userData } = response.data;
      
      // Store tokens and user data
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
      
      const expiryTime = Date.now() + tokens.expiresIn * 1000;
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

      setUser(userData);
      setToken(tokens.accessToken);

      // Schedule automatic token refresh
      scheduleTokenRefresh(tokens.expiresIn);

      // Connect WebSocket with access token
      websocketClient.connect(tokens.accessToken);
    } catch (error) {
      clearAuthData();
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint (optional, for server-side cleanup)
      await post('/auth/logout', {});
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with client-side cleanup even if API call fails
    } finally {
      clearAuthData();
    }
  };

  const updateUser = (userData: User) => {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
    setUser(userData);
  };

  const startImpersonation = useCallback((targetUser: User, expiresAt: string) => {
    if (!user) return;

    const state: ImpersonationState = {
      isImpersonating: true,
      impersonatedUser: targetUser,
      realUser: user,
      expiresAt,
    };
    localStorage.setItem(IMPERSONATION_STATE_KEY, JSON.stringify(state));

    setRealUser(user);
    setImpersonatedUser(targetUser);
    setIsImpersonating(true);
    setUser(targetUser);
  }, [user]);

  const stopImpersonation = useCallback(() => {
    localStorage.removeItem(IMPERSONATION_STATE_KEY);

    if (realUser) {
      setUser(realUser);
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(realUser));
    }
    setIsImpersonating(false);
    setImpersonatedUser(null);
    setRealUser(null);
  }, [realUser]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    token,
    login,
    logout,
    updateUser,
    refreshAccessToken,
    isImpersonating,
    impersonatedUser,
    realUser,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
